import React from 'react';

import { PdfRenderer } from '@/components/templates-v2/document-generation/pdf/renderer/PdfFormRenderer';
import { Text } from '@/components/ui/catalyst/text';

/**
 * Component to render a PDF from a URL
 * Uses a fixed max-width to prevent flickering on resize
 */
interface RenderedPdfPreviewProps {
  url: string;
}

const PREVIEW_WIDTH = 700;

const stripQueryParams = (url: string): string => {
  try {
    const urlObj = new URL(url, window.location.origin);
    return `${urlObj.origin}${urlObj.pathname}`;
  } catch {
    // If URL parsing fails, return as-is
    return url;
  }
};

export const RenderedPdfPreview: React.FC<RenderedPdfPreviewProps> = ({
  url,
}) => {
  const [pdfBlob, setPdfBlob] = React.useState<Blob | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [fetchedUrl, setFetchedUrl] = React.useState<string | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);

  React.useEffect(() => {
    const element = containerRef.current;
    if (!element) return undefined;

    const updateWidth = () => {
      setContainerWidth(element.getBoundingClientRect().width);
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    // Only fetch if URL is different from what we already fetched (ignoring query params)
    // This prevents re-downloading the same PDF during polling
    if (!url) return;

    const normalizedUrl = stripQueryParams(url);
    const normalizedFetchedUrl = fetchedUrl
      ? stripQueryParams(fetchedUrl)
      : null;

    if (normalizedFetchedUrl === normalizedUrl) {
      return;
    }

    const fetchPdf = async () => {
      try {
        setLoading(true);
        const response = await fetch(url);
        const blob = await response.blob();
        setPdfBlob(blob);
        setFetchedUrl(url);
      } catch (error) {
        // Failed to fetch PDF
        setFetchedUrl(url); // Mark as attempted to avoid retry loops
      } finally {
        setLoading(false);
      }
    };
    fetchPdf();
  }, [url, fetchedUrl]);

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  if (!pdfBlob) {
    return (
      <div className="h-96 flex items-center justify-center">
        <Text className="text-sm text-gray-500">Failed to load preview</Text>
      </div>
    );
  }

  const renderWidth = containerWidth
    ? Math.max(Math.min(containerWidth - 24, 1180), 640)
    : PREVIEW_WIDTH;

  return (
    <div ref={containerRef} className="w-full px-3 py-4">
      {pdfBlob && <PdfRenderer file={pdfBlob} renderWidth={renderWidth} />}
    </div>
  );
};

// Wrap with memo to prevent remounting during polling
export const MemoizedRenderedPdfPreview = React.memo(
  RenderedPdfPreview,
  (prev, next) => {
    // Only re-render if the base URL changes (strip query params)
    const prevUrl = stripQueryParams(prev.url);
    const nextUrl = stripQueryParams(next.url);
    return prevUrl === nextUrl;
  },
);
